import { Button, Row, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import IconInfo from '@/assets/icons/icon-info.svg?react';
import IconEye from '@/assets/icons/icon-view.svg?react';
import { StorageSystem } from '@/types/storageSystem';

type Props = {
  prepareSimulationItem: (id: string, type: string) => void;
};

const useNewSimulationStorageSystemTableColumns = (props: Props) => {
  const { t } = useTranslation();
  const columns: ColumnsType<StorageSystem> = [
    {
      title: t('storageSystem.table.name'),
      dataIndex: 'name',
      key: 'name',
      width: 250,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => (
        <Row align="middle">
          <Typography.Paragraph
            style={{ maxWidth: '150px' }}
            className="text"
            ellipsis={{
              rows: 1,
              tooltip: name,
              expandable: false,
            }}
          >
            {name}
          </Typography.Paragraph>
        </Row>
      ),
    },
    {
      title: t('storageSystem.table.description'),
      dataIndex: 'description',
      key: 'description',
      width: 150,
      render: (description) => (
        <Typography.Paragraph
          style={{ maxWidth: '100px' }}
          className="text"
          ellipsis={{
            rows: 1,
            tooltip: description,
            expandable: false,
          }}
        >
          {description}
        </Typography.Paragraph>
      ),
    },
    {
      title: (
        <Tooltip
          className="cursor-pointer"
          trigger="click"
          title={t('storageSystem.table.tooltip.ratedEnergyDesc')}
        >
          <Row align="middle" justify="start" className="w-100">
            <Typography.Text className="pr-8">
              {t('storageSystem.table.ratedEnergy')}
            </Typography.Text>
            <IconInfo fill="black" />
          </Row>
        </Tooltip>
      ),
      dataIndex: 'ratedEnergyMwh',
      key: 'ratedEnergyMwh',
      sorter: (a, b) => a.ratedEnergyMwh - b.ratedEnergyMwh,
      render: (ratedEnergyMwh) => (
        <Row align="middle" justify="end">
          <Typography.Text>{ratedEnergyMwh}</Typography.Text>
        </Row>
      ),
    },
    {
      title: (
        <Tooltip
          className="cursor-pointer"
          trigger="click"
          title={t('storageSystem.table.tooltip.ratedPowerDesc')}
        >
          <Row align="middle" justify="start" className="w-100">
            <Typography.Text className="pr-8">
              {t('storageSystem.table.ratedPower')}
            </Typography.Text>
            <IconInfo fill="black" />
          </Row>
        </Tooltip>
      ),
      dataIndex: 'ratedPowerMw',
      key: 'ratedPowerMw',
      sorter: (a, b) => a.ratedPowerMw - b.ratedPowerMw,

      render: (ratedPowerMw) => (
        <Row align="middle" justify="end">
          <Typography.Text>{ratedPowerMw}</Typography.Text>
        </Row>
      ),
    },
    {
      title: (
        <Tooltip
          className="cursor-pointer"
          trigger="click"
          title={t('storageSystem.table.tooltip.creationDateDesc')}
        >
          <Row align="middle" justify="start" className="w-100">
            <Typography.Text className="pr-8">
              {t('storageSystem.table.creationDate')}
            </Typography.Text>
            <IconInfo fill="black" />
          </Row>
        </Tooltip>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      width: 180,
      render: (dataIndex) => moment(dataIndex).format('DD.MM.YYYY HH:mm'),
      sorter: (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      align: 'center',
      width: '50px',
      render: (_, storage) => (
        <Row align="middle" justify="space-between">
          <Tooltip title={t('storageSystem.table.view')}>
            <Button
              className="default-icon-button"
              type="default"
              onClick={() => {
                props.prepareSimulationItem(storage.id, 'Storage');
              }}
              icon={<IconEye />}
            />
          </Tooltip>
        </Row>
      ),
    },
  ];

  return columns;
};

export default useNewSimulationStorageSystemTableColumns;
